import { gql } from '@apollo/client';
import { MediaQueries } from '@minecraft.shared-queries';

export const GET_PRESENTATION_PUBLIC = gql`
  query getPresentationPublic($guid: String!, $withShareSelectsData: Boolean! = false) {
    getPresentationPublic(guid: $guid) {
      __typename
      id
      name
      linkExpiresAt
      linkExpired
      guid
      settings {
        __typename
        allowDownloadMedia
        leaveFeedback
        seeOthersFeedback
        allowExportFormResponse
      }
      organization {
        __typename
        name
        parentName
        id
        logo {
          __typename
          id
          mediaId
          filename
          fileKey
        }
      }
      type {
        __typename
        id
        code
        name
      }
      folders {
        __typename
        id
        talentCount @skip(if: $withShareSelectsData)
        submissionCount @include(if: $withShareSelectsData)
        groupCount
        name
        auditionSession {
          __typename
          id
          requestForms {
            __typename
            requestId
            forms {
              __typename
              formId
              title
            }
          }
        }
      }
      project {
        __typename
        name
        internalName
        id
      }
    }
  }
`;

export const QUERY_PRESENTATION_PUBLIC_FOLDER = gql`
  query getPresentationPublic(
    $guid: String!
    $folderId: [Int!]!
    $pageInput: PageInput!
    $withShareSelectsData: Boolean! = false
  ) {
    getPresentationPublic(guid: $guid) {
      __typename
      id
      name
      organization {
        __typename
        name
        parentName
        logo {
          __typename
          id
          filename
          fileKey
        }
      }
      type {
        __typename
        id
        code
        name
      }
      linkExpiresAt
      linkExpired
      guid
      folders {
        __typename
        id
        name
        talentCount @skip(if: $withShareSelectsData)
        submissionCount @include(if: $withShareSelectsData)
      }
      project @include(if: $withShareSelectsData) {
        __typename
        name
        internalName
      }
      folder(folderId: $folderId) {
        __typename
        id
        name
        talentCount @skip(if: $withShareSelectsData)
        submissionCount @include(if: $withShareSelectsData)
        talents(pageInput: $pageInput) @skip(if: $withShareSelectsData) {
          __typename
          id
          guid
          submissionId
          pickCount
          pickedBy {
            __typename
            id
            userName
          }
          cover {
            ...CoverMedia
          }
          talentUnions {
            __typename
            id
            code
            name
          }
          minor
          dateOfBirth
          stageName
          roleName
        }
        groups @include(if: $withShareSelectsData) {
          __typename
          id
          submissions {
            __typename
            id
            note
            mediaCount {
              __typename
              count
              type {
                __typename
                id
                code
              }
            }
            cover {
              ...CoverMedia
            }
            profile {
              __typename
              id
              guid
              minor
              dateOfBirth
              stageName
              defaultSubmissionNote
              cover {
                ...CoverMedia
              }
              talentUnions {
                __typename
                id
                code
                name
                parentObject {
                  __typename
                  id
                  name
                }
              }
              isPersonal
              profileMainDivision {
                __typename
                id
                name
                abbreviation
              }
              profileMainOrganization {
                __typename
                id
                name
                abbreviation
              }
            }
            roleId
            roleName
          }
        }
      }
    }
  }
  ${MediaQueries.coverFragment}
`;

export const groupTalentFragment = gql`
  fragment GroupTalentPublic on PresentationTalentPublic {
    __typename
    id
    submissionId
    cover {
      __typename
      ...MediaAssetWithThumbnail
    }
    guid
    stageName
    dateOfBirth
    roleGuid
    projectGuid
    roleName
    pickCount
    minor
    commentCount
    pickedBy {
      __typename
      id
      userName
    }
  }
  ${MediaQueries.mediaAssetWithThumbnailFragment}
`;

export const GET_PRESENTATION_PUBLIC_FOLDER = gql`
  query getPresentationPublicFolder($hasPicks: Boolean, $guid: String!, $folderId: [Int!]!) {
    getPresentationPublic(guid: $guid) {
      id
      folder(folderId: $folderId) {
        id
        name
        talentCount
        talentsWithPicksCount
        order
        groups(hasPicks: $hasPicks) {
          id
          name
          stageNames
          talentCount
          order
          cover {
            ...CoverMedia
            transformation {
              startOffset
            }
          }
        }
      }
    }
  }
  ${MediaQueries.coverFragment}
`;

export const GET_PRESENTATION_BY_FILTERS = gql`
  query getPresentationByFilters($input: PresentationFilterInput!, $folderId: [Int!]!) {
    getPresentationByFilters(input: $input) {
      __typename
      id
      folder(folderId: $folderId) {
        __typename
        id
        name
        talentCount
        talentsWithPicksCount
        order
        groups: groupsByFilter(input: $input) {
          __typename
          id
          name
          stageNames
          talentCount
          order
          cover {
            ...CoverMedia
            transformation {
              __typename
              startOffset
            }
          }
        }
      }
    }
  }
  ${MediaQueries.coverFragment}
`;

export const GET_PRESENTATION_PUBLIC_GROUP = gql`
  query getPresentationGroupPublic(
    $hasPicks: Boolean
    $guid: String!
    $groupId: Int!
    $userName: String
    $withFeedback: Boolean! = true
  ) {
    getPresentationGroupPublic(guid: $guid, groupId: $groupId) {
      id
      name
      stageNames
      talentCount
      media {
        ...PublicPresentationMedia
      }
      talents(hasPicks: $hasPicks, userName: $userName) {
        id
        submissionId
        cover {
          ...MediaAssetWithThumbnail
        }
        guid
        stageName
        roleName
        pickCount @include(if: $withFeedback)
        commentCount @include(if: $withFeedback)
        pickedBy @include(if: $withFeedback) {
          id
          userName
        }
      }
    }
  }
  ${MediaQueries.mediaAssetWithThumbnailFragment}
  ${MediaQueries.publicPresentationMediaFragment}
`;

export const GET_PRESENTATION_PUBLIC_GROUP_BY_FILTERS = gql`
  query getPresentationGroupByFilters(
    $guid: String!
    $groupId: Int!
    $userName: String
    $input: PresentationFilterInput!
  ) {
    getPresentationGroupByFilters(guid: $guid, groupId: $groupId, input: $input) {
      __typename
      id
      name
      stageNames
      talentCount
      media {
        __typename
        ...PublicPresentationMedia
      }
      talents(userName: $userName) {
        __typename
        ...GroupTalentPublic
      }
      submissions {
        __typename
        id
        roleId
      }
    }
  }
  ${MediaQueries.publicPresentationMediaFragment}
  ${groupTalentFragment}
`;

export const MUTATION_CREATE_PRESENTATION_LOG_REPORT = gql`
  mutation createPresentationLogReportPublic($input: PresentationReportCreatePublicInput!) {
    presentationReportId: createPresentationLogReportPublic(input: $input)
  }
`;

export const MUTATION_CREATE_PRESENTATION_SIZE_SHEETS_REPORT = gql`
  mutation createPresentationSizeSheetsReportPublic($input: PresentationReportCreatePublicInput!) {
    presentationReportId: createPresentationSizeSheetsReportPublic(input: $input)
  }
`;
